import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CalendarRange, Loader2, Plus, Trash2, RotateCcw, Save, ChevronDown } from "lucide-react";

const MONTHS = ["Januar", "Februar", "Marts", "April", "Maj", "Juni", "Juli", "August", "September", "Oktober", "November", "December"];

function Card({ children, className = "", ...p }) { return <div className={`bg-white rounded-2xl border border-[#EBE8E3] ${className}`} {...p}>{children}</div>; }

function PointEditor({ p, onChange, onRemove, withOwner }) {
  return (
    <div className="rounded-xl border border-[#F0EDE8] bg-[#FBFAF8] p-3 space-y-2">
      <div className="flex gap-2">
        <Input value={p.title || ""} onChange={(e) => onChange({ title: e.target.value })} placeholder="Punkt-titel" className="rounded-lg h-9 text-sm bg-white" />
        {withOwner && <Input value={p.owner_label || ""} onChange={(e) => onChange({ owner_label: e.target.value })} placeholder="Ansvarlig" className="rounded-lg h-9 text-sm w-28 bg-white" />}
        <button onClick={onRemove} className="text-[#A83232] hover:text-[#8f2a2a] shrink-0 self-center"><Trash2 className="h-4 w-4" /></button>
      </div>
      <Textarea value={p.help || ""} onChange={(e) => onChange({ help: e.target.value })} rows={2} placeholder="Hjælpetekst / uddybning (vises som vejledning på mødet)" className="rounded-lg text-xs bg-white" />
    </div>
  );
}

export default function LeYearWheel() {
  const [cfg, setCfg] = useState(null);
  const [months, setMonths] = useState({});
  const [events, setEvents] = useState([]);
  const [openMonth, setOpenMonth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/le/yearwheel"); setCfg(r.data); setMonths(r.data.months || {}); setEvents(r.data.events || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const setPoint = (m, i, patch) => setMonths((prev) => ({ ...prev, [m]: (prev[m] || []).map((p, j) => j === i ? { ...p, ...patch } : p) }));
  const addPoint = (m) => { setMonths((prev) => ({ ...prev, [m]: [...(prev[m] || []), { title: "", owner_label: "", help: "" }] })); setOpenMonth(m); };
  const removePoint = (m, i) => setMonths((prev) => ({ ...prev, [m]: (prev[m] || []).filter((_, j) => j !== i) }));

  const setEvent = (i, patch) => setEvents((prev) => prev.map((p, j) => j === i ? { ...p, ...patch } : p));
  const addEvent = () => setEvents((prev) => [...prev, { title: "", help: "" }]);
  const removeEvent = (i) => setEvents((prev) => prev.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(months).forEach(([m, pts]) => { const f = (pts || []).filter((p) => (p.title || "").trim()); if (f.length) clean[m] = f; });
      const r = await api.put("/le/yearwheel", { months: clean, events: events.filter((e) => (e.title || "").trim()) });
      setCfg(r.data); setMonths(r.data.months || {}); setEvents(r.data.events || []);
      toast.success("Årshjul gemt. Gælder nye møder.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const reset = async () => {
    if (!window.confirm("Nulstil årshjulet til standardopsætningen?")) return;
    try { const r = await api.post("/le/yearwheel/reset"); setCfg(r.data); setMonths(r.data.months || {}); setEvents(r.data.events || []); toast.success("Nulstillet til standard"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="flex items-center gap-2 text-stone-400"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser…</div>;

  return (
    <div className="space-y-6" data-testid="le-yearwheel-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="label-caps flex items-center gap-2 text-[#806B61]"><CalendarRange className="h-3.5 w-3.5" /> Ledelsens evaluering</p>
          <h1 className="text-4xl font-semibold text-[#1A1919] mt-1">Dagsorden / årshjul</h1>
          <p className="text-stone-500 mt-2 max-w-2xl">Bestem hvilke evalueringspunkter der automatisk indsættes på møderne hver måned. Ændringer gælder kun <span className="text-stone-700 font-medium">nye</span> møder — færdige referater røres ikke.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} className="rounded-xl border-[#EBE8E3]" data-testid="le-yearwheel-reset"><RotateCcw className="h-4 w-4 mr-1.5" /> Nulstil</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="le-yearwheel-save"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Gemmer…" : "Gem"}</Button>
        </div>
      </div>

      {/* Faste punkter */}
      <Card className="p-5">
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#A8A29E]">Faste punkter — altid på hvert møde</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {(cfg?.fixed || []).map((f, i) => (
            <span key={f.key} className="inline-flex items-center gap-1.5 text-sm rounded-full bg-[#F3F1EC] text-stone-700 px-3 py-1.5">
              <span className="h-5 w-5 rounded-full bg-white text-[11px] font-semibold flex items-center justify-center text-[#806B61]">{i + 1}</span>{f.title}
            </span>
          ))}
        </div>
      </Card>

      {/* Månedspunkter */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#A8A29E] mb-3">Månedspunkter — indsættes under «Punkter fra ledelsens evaluering»</p>
        <div className="space-y-2.5">
          {MONTHS.map((name, idx) => {
            const m = String(idx + 1);
            const pts = months[m] || [];
            const open = openMonth === m;
            const isNow = idx === new Date().getMonth();
            return (
              <Card key={m} className={`overflow-hidden ${isNow ? "border-[#A83232] ring-1 ring-[#E9C9C3]" : ""}`} data-testid={`le-month-card-${m}`}>
                <button type="button" data-testid={`le-month-toggle-${m}`} onClick={() => setOpenMonth(open ? null : m)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${open ? "bg-[#FBF6F5]" : "hover:bg-[#FBFAF8]"}`}>
                  <span className="text-sm font-semibold text-[#1A1919] w-24 flex items-center gap-1.5">{name}{isNow && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#A83232] text-white" data-testid="le-month-current">NU</span>}</span>
                  <span className="flex-1 text-sm text-stone-500 truncate">{pts.length === 0 ? "Ingen faste punkter" : pts.map((p) => p.title).filter(Boolean).join(" · ")}</span>
                  {pts.length > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F1DEDA] text-[#A83232]">{pts.length}</span>}
                  <ChevronDown className={`h-4 w-4 text-stone-400 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && <div className="px-4 pb-4 border-t border-[#F0EDE8] pt-3 space-y-3" data-testid={`le-month-${m}`}>
                  {pts.map((p, i) => <PointEditor key={i} p={p} withOwner onChange={(patch) => setPoint(m, i, patch)} onRemove={() => removePoint(m, i)} />)}
                  <button onClick={() => addPoint(m)} data-testid={`le-month-add-${m}`} className="text-sm text-[#806B61] hover:underline flex items-center gap-1"><Plus className="h-4 w-4" /> Tilføj punkt til {name}</button>
                </div>}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Hændelsesbestemte */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#A8A29E] mb-3">Hændelsesbestemte punkter — tilføjes efter behov på det enkelte møde</p>
        <Card className="p-5 space-y-3">
          {events.length === 0 && <p className="text-sm text-stone-400">Ingen hændelsesbestemte punkter.</p>}
          {events.map((p, i) => <PointEditor key={i} p={p} onChange={(patch) => setEvent(i, patch)} onRemove={() => removeEvent(i)} />)}
          <button onClick={addEvent} data-testid="le-event-add" className="text-sm text-[#806B61] hover:underline flex items-center gap-1"><Plus className="h-4 w-4" /> Tilføj punkt</button>
        </Card>
      </div>
    </div>
  );
}
